import { useMemo, useState } from 'react';
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  PaymentMethod,
  StripeElementsOptionsMode,
  StripeError,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { trackGA4Event } from 'common/utils/ga4';
import { STRIPE_PUBLISHABLE_KEY } from 'config/stripe';
import { getInitCreditIntent, setPaymentMethod } from 'service/payment';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

type StripeExpressCheckoutProps = {
  disabled?: boolean;
  showDivider?: boolean;
  walletTrackingContext?:
    | 'membership'
    | 'prime'
    | 'tipping'
    | 'course'
    | 'add_credit_card';
  onComplete: (
    paymentMethod: string | PaymentMethod,
    expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType'],
  ) => Promise<void>;
  onBeforeConfirm?: (
    expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType'],
  ) => Promise<boolean>;
  onBeforeClick?: (
    expressPaymentType: StripeExpressCheckoutElementClickEvent['expressPaymentType'],
  ) => boolean | Promise<boolean>;
  onError?: (
    error: Error | StripeError,
    expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType'],
  ) => void;
  onStart?: (
    expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType'],
  ) => void;
};

const trackWalletClick = (
  expressPaymentType: StripeExpressCheckoutElementClickEvent['expressPaymentType'],
  walletTrackingContext?: StripeExpressCheckoutProps['walletTrackingContext'],
) => {
  if (expressPaymentType === 'apple_pay') {
    trackGA4Event('click_apple_pay', {
      payment_context: walletTrackingContext || null,
    });
    return;
  }

  if (expressPaymentType === 'google_pay') {
    trackGA4Event('click_google_pay', {
      payment_context: walletTrackingContext || null,
    });
  }
};

const StripeExpressCheckoutContent = ({
  disabled,
  showDivider = true,
  walletTrackingContext,
  onComplete,
  onBeforeClick,
  onBeforeConfirm,
  onError,
  onStart,
}: StripeExpressCheckoutProps) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    setIsAvailable(!!event.availablePaymentMethods);
  };

  const handleConfirm = async (
    event: StripeExpressCheckoutElementConfirmEvent,
  ) => {
    if (!stripe || !elements || disabled) {
      event.paymentFailed({ reason: 'fail' });
      return;
    }

    try {
      onStart?.(event.expressPaymentType);
      const canContinue = onBeforeConfirm
        ? await onBeforeConfirm(event.expressPaymentType)
        : true;
      if (!canContinue) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw submitError;
      }

      const getIntentResponse = await getInitCreditIntent();
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: getIntentResponse.data.client_secret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (error) {
        throw error;
      }
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        throw new Error('Failed to create payment method');
      }
      await setPaymentMethod(setupIntent.payment_method);
      try {
        await onComplete(setupIntent.payment_method, event.expressPaymentType);
      } catch {
        event.paymentFailed({ reason: 'fail' });
      }
    } catch (error) {
      event.paymentFailed({ reason: 'fail' });
      onError?.(
        error instanceof Error ? error : new Error('Express checkout failed'),
        event.expressPaymentType,
      );
    }
  };

  return (
    <div
      className={isAvailable ? 'mb-3' : 'hidden'}
      style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
    >
      <ExpressCheckoutElement
        onReady={handleReady}
        onClick={async (event) => {
          const canContinue = onBeforeClick
            ? await onBeforeClick(event.expressPaymentType)
            : true;
          if (!canContinue) return;

          trackWalletClick(event.expressPaymentType, walletTrackingContext);
          event.resolve({
            business: {
              name: 'PostPrime',
            },
          });
        }}
        onConfirm={handleConfirm}
        options={{
          buttonHeight: 44,
          buttonType: {
            applePay: 'check-out',
            googlePay: 'checkout',
          },
          layout: {
            maxColumns: 1,
            maxRows: 2,
          },
          paymentMethodOrder: ['applePay', 'googlePay'],
          paymentMethods: {
            applePay: 'always',
            googlePay: 'always',
            link: 'never',
          },
        }}
      />
      {showDivider && (
        <div className="my-4 flex items-center text-center text-xs leading-[18px] text-[#cccccc] before:h-px before:flex-1 before:bg-[#cccccc59] before:content-[''] after:h-px after:flex-1 after:bg-[#cccccc59] after:content-['']">
          <span>または</span>
        </div>
      )}
    </div>
  );
};

const StripeExpressCheckout = ({
  disabled,
  showDivider,
  walletTrackingContext,
  onComplete,
  onBeforeClick,
  onBeforeConfirm,
  onError,
  onStart,
}: StripeExpressCheckoutProps): JSX.Element => {
  const options = useMemo<StripeElementsOptionsMode>(
    () => ({
      mode: 'setup',
      currency: 'jpy',
      paymentMethodTypes: ['card'],
    }),
    [],
  );

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripeExpressCheckoutContent
        disabled={disabled}
        showDivider={showDivider}
        walletTrackingContext={walletTrackingContext}
        onComplete={onComplete}
        onBeforeClick={onBeforeClick}
        onBeforeConfirm={onBeforeConfirm}
        onError={onError}
        onStart={onStart}
      />
    </Elements>
  );
};

export default StripeExpressCheckout;
